<template>
  <section class="main">
    <div v-if="isDeviceLinked && hasUuid">
      <!-- Mode for linked devices with UUID parameter -->
      <div class="loader-container">
        <loader size="50px" />
        <p class="processing-text">{{ $t('AUTHORIZING_ANOTHER_DEVICE') }}</p>
      </div>
    </div>
    <div v-else-if="isDeviceLinked && !hasUuid">
      <!-- Mode for linked devices WITHOUT UUID parameter -->
      <div class="message-container">
        <h1 class="title">{{ $t('NOTICE_SCAN_QR_CODE_THAT_IS_SHOWN_ON_COMPUTER') }}</h1>
      </div>
    </div>
    <div v-else>
      <!-- Mode for unlinked devices showing QR code -->
      <h1 class="title">{{ $t('SCAN_QR_TO_LINK_COMPUTER') }}</h1>
      <div class="subtitle-wrapper">
        <html-with-links tag="p" class="subtitle" :html="$t('LINK_APPLE_SILICON_MAC_DESC', { href: '/appdb-tools' })" />
        <html-with-links tag="p" class="subtitle tv-link" :html="$t('CLICK_TO_LINK_TV', { href: '/link/tv' })" />
      </div>

      <!-- Show loader while fetching new QR code -->
      <div v-if="isLoading" class="loader-container">
        <loader size="50px" />
        <p class="processing-text">{{ $t('LOADING') }}</p>
      </div>

      <!-- Show QR code once loaded -->
      <div v-else>
        <div class="qr-container">
          <qrcode class="qr-code" :value="qrCodeUrl" :background="qrCodeBg" />
          <div class="timer" v-if="expiresTime">
            {{ timeRemaining }}
          </div>
        </div>

        <!-- Email-based linking option -->
        <div class="email-linking">
          <p class="email-linking-text">{{ $t('LINK_DEVICE_BACKUP_OPTION') }}</p>

          <div class="form__group g-code">
            <input v-model="email" placeholder="user@example.com" type="email" class="g-code__input" />
            <button type="button" class="g-code__button" :class="{ 'g-code__button_loading': isEmailLinking }"
              @click="linkViaEmail">
              <span class="g-code__loader"></span>
              {{ $t('LINK_DEVICE_BUTTON') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Back link shown in all modes -->
    <div class="back-link-container">
      <a href="javascript:history.back()" class="g-button back-link">{{ $t('GO_BACK') }}</a>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import QrcodeVue from 'qrcode.vue';
import Loader from '@/components/Loader';
import HtmlWithLinks from '@/components/HtmlWithLinks.vue';

export default {
  components: { qrcode: QrcodeVue, Loader, HtmlWithLinks },
  data() {
    return {
      uuid: '',
      expiresTime: null,
      linkToken: null,
      isLoading: true,
      pollingInterval: null,
      checkSessionInterval: null,
      lastUpdated: Date.now(),
      email: '',
      isEmailLinking: false,
    };
  },
  computed: {
    ...mapState(['theme']),
    qrCodeBg() {
      // Always return white for QR code background regardless of theme
      return '#FFFFFF';
    },
    isDeviceLinked() {
      return this.$store.getters.isDeviceLinked;
    },
    hasUuid() {
      return !!this.$route.query.uuid;
    },
    domain() {
      return window.location.host;
    },
    qrCodeUrl() {
      if (!this.uuid) return '';
      return `https://${this.domain}/link-qr/?uuid=${this.uuid}`;
    },
    timeRemaining() {
      if (!this.expiresTime) return '';

      const timestamp = this.lastUpdated;
      const now = Math.floor(Date.now() / 1000);
      const remaining = Math.max(0, this.expiresTime - now);

      return `${remaining} ${this.$t('SECONDS_REMAINING')}`;
    },
  },
  methods: {
    async getLinkSession() {
      try {
        // Clean up any existing intervals/timeouts first
        this.cleanupIntervals();

        // Set loading state
        this.isLoading = true;

        // Clear previous state
        this.uuid = '';
        this.expiresTime = null;
        this.linkToken = null;

        // Get new session
        const response = await this.$api.call('get_link_session', {}, false);

        if (response.success && response.data) {
          this.uuid = response.data.uuid || '';
          this.expiresTime = response.data.expires_at || null;
          this.linkToken = response.data.link_token || null;

          // Start checking for session updates
          this.startCheckingSession();

          // End loading state
          this.isLoading = false;
        } else {
          console.error('Failed to get link session:', response);
          setTimeout(() => this.getLinkSession(), 2000);
        }
      } catch (error) {
        console.error('Error getting link session:', error);
        setTimeout(() => this.getLinkSession(), 2000);
      }
    },

    startCheckingSession() {
      if (this.checkSessionInterval) {
        clearInterval(this.checkSessionInterval);
        this.checkSessionInterval = null;
      }

      // Instead of interval, use recursive setTimeout pattern
      this.checkSession();
    },

    async checkSession() {
      if (!this.uuid) return;

      try {
        const response = await this.$api.call('get_link_session', { uuid: this.uuid }, false);

        if (response.success && response.data) {
          // Update expires_at from response to recalculate remaining time
          if (response.data.expires_at) {
            this.expiresTime = response.data.expires_at;
            this.lastUpdated = Date.now();
          }

          // Check if we got a link token
          if (response.data.link_token) {
            // We have a link token, set it and redirect
            localStorage.setItem('lt', response.data.link_token);
            this.$store.commit('lt', response.data.link_token);

            // Fetch all necessary data before redirecting
            await Promise.all([
              this.$store.dispatch('getConfig'),
              this.$store.dispatch('getDevices'),
              this.$store.dispatch('getCustomerProfile'),
            ]);

            // Now redirect to success page
            this.$router.push('/success?title=PC_LINK_OK');

            // No need to continue checking
            return;
          }
        } else if (response.errors && response.errors[0] && response.errors[0].code === 'ERROR_LINK_SESSION_EXPIRED') {
          // Session expired - CLEAN UP ALL INTERVALS before getting a new session
          this.cleanupIntervals();
          // Get a new session
          this.getLinkSession();
          return;
        }
      } catch (error) {
        console.error('Error checking link session:', error);
      }

      // Force update of timer display even if expires_at didn't change
      this.lastUpdated = Date.now();

      // Store timeout ID for cleanup
      this.checkSessionInterval = setTimeout(() => {
        this.checkSession();
      }, 1000);
    },

    async processLinkFromDevice() {
      try {
        if (!this.$store.getters.lt || !this.$route.query.uuid) {
          // Should not happen, redirect to home
          this.$router.push('/');
          return;
        }

        const response = await this.$api.call('get_link_session', {
          uuid: this.$route.query.uuid,
          set_link_token: this.$store.getters.lt,
        }, false);

        if (response.success) {
          // Fetch all necessary data before redirecting
          await Promise.all([
            this.$store.dispatch('getConfig'),
            this.$store.dispatch('getDevices'),
            this.$store.dispatch('getCustomerProfile'),
          ]);

          // Successfully linked, redirect to success page
          this.$router.push('/success?title=LINK_AUTHORIZED');
        } else {
          // Error linking, redirect to home
          this.$router.push('/');
        }
      } catch (error) {
        console.error('Error processing link from device:', error);
        this.$router.push('/');
      }
    },

    // Helper method to clean up all intervals and timeouts
    cleanupIntervals() {
      if (this.checkSessionInterval) {
        clearTimeout(this.checkSessionInterval);
        this.checkSessionInterval = null;
      }

      if (this.pollingInterval) {
        clearTimeout(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    async linkViaEmail() {
      if (!/^\S+@\S+\.\S+$/.test(this.email)) {
        this.$notify({
          type: 'error',
          text: this.$t('INVALID_EMAIL'),
        });
        return;
      }

      this.isEmailLinking = true;

      this.$api.link({
        email: this.email,
        type: 'control',
      })
        .then((response) => {
          if (response.errors.length && response.errors[0].code === 'ERROR_PROCEED_TO_EMAIL') {
            this.$router.push({
              name: 'success',
              params: {
                message: response.errors[0].translated,
                link: '/',
                linkText: this.$t('GO_TO_MAIN_PAGE'),
              },
            });
            return;
          }

          if (response.success) {
            this.$router.push({
              name: 'success',
              params: {
                message: this.$t('YOUR_DEVICE_BEING_LINKED'),
                link: '/',
                linkText: this.$t('GO_TO_MAIN_PAGE'),
              },
            });
            if (response.data.link_token) {
              this.$store.commit('lt', response.data.link_token);
            }
            if (response.data.profile_service) {
              this.checkDevicesAfterLink();
              window.setTimeout(() => {
                window.location.href = response.data.profile_service;
              }, 1000);
            }
          } else {
            this.$router.push({
              name: 'error',
              params: {
                message: response.errors[0].translated,
              },
            });
          }
        })
        .finally(() => {
          this.isEmailLinking = false;
        });
    },

    checkDevicesAfterLink() {
      setTimeout(async () => {
        await Promise.allSettled([this.$store.dispatch('getDevices', false), this.$store.dispatch('getConfig', false)]);

        const { devices } = this.$store.getters;
        if (devices.length > 0) {
          this.$router.push('/');
          return;
        }

        this.checkDevicesAfterLink();
      }, 1000);
    },
  },
  created() {
    // Always reset the loading state when component is created
    this.isLoading = true;

    if (this.isDeviceLinked && this.hasUuid) {
      // Mode for linked devices with UUID parameter
      this.processLinkFromDevice();
    } else if (!this.isDeviceLinked) {
      // Mode for unlinked devices showing QR code
      this.getLinkSession();
    }
    // Third mode (isDeviceLinked && !hasUuid) just shows the message, no action needed
  },
  beforeDestroy() {
    // Clean up intervals/timeouts
    this.cleanupIntervals();
  },
  // Handle page navigation with keep-alive
  activated() {
    // If we're in the QR code mode and not linked, refresh the QR code
    if (!this.isDeviceLinked && !this.hasUuid) {
      this.getLinkSession();
    }
  },
  // Ensure cleanup when navigating away
  deactivated() {
    // Clean up all intervals and timeouts when navigating away
    this.cleanupIntervals();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.main {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
  min-height: 300px;
}

.title {
  font-size: 22px;
  margin-bottom: 30px;
  text-align: center;
  max-width: 600px;
}

.qr-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.qr-code {
  width: 250px;
  height: 250px;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
  align-items: center;

  // Ensure QR code SVG/Canvas fills container
  :deep(svg),
  :deep(canvas) {
    width: 100% !important;
    height: 100% !important;
    display: block;
  }
}

.timer {
  font-size: 18px;
  margin-top: 10px;
  color: var(--text-secondary);
}

.loader-container,
.message-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px;
}

.processing-text {
  margin-top: 20px;
  font-size: 18px;
}

.back-link-container {
  margin-top: 30px;
  text-align: center;
}

.back-link {
  font-weight: 400;
  line-height: normal;
  font-family: inherit;
}

.subtitle-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: -20px;
  margin-bottom: 30px;
}

.subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  text-align: center;
  max-width: 500px;
  margin: 0 auto;

  :deep(a) {
    color: var(--primary);
  }
}

.tv-link {
  margin-top: 8px;
}

.email-linking {
  margin-top: 30px;
  width: 100%;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.email-linking-text {
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 15px;
  text-align: center;
}

.form__group {
  display: flex;
  width: 100%;
  position: relative;
  margin-bottom: 10px;
}

.g-code {
  &__input {
    background-color: var(--tertiary-bg);
    border: 1px solid var(--tertiary-accent);
    border-radius: 5px 0 0 5px;
    padding: 10px 10px;
    font-size: 14px;
    width: 100%;
    color: var(--text-primary);
    height: 40px;
    outline: none;

    &:focus {
      border-color: var(--primary);
    }
  }

  &__button {
    background-color: var(--primary);
    color: #fff;
    border: none;
    border-radius: 0 5px 5px 0;
    padding: 0 15px;
    font-size: 14px;
    cursor: pointer;
    white-space: nowrap;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s ease;

    &_loading {
      position: relative;
      pointer-events: none;

      .g-code__loader {
        display: inline-block;
      }
    }
  }

  &__loader {
    display: none;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: #fff;
    animation: spin 1s linear infinite;
    margin-right: 5px;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(360deg);
    }
  }
}
</style>
